import {
  ModIconButton,
  ModMenuItem,
  ModTooltip
} from '@moderneinc/design-system-components';
import { iconSize } from '@moderneinc/design-system-tokens';
import { ClickAwayListener, MenuList, Popper } from '@mui/material';
import { bindPopper, bindToggle } from 'material-ui-popup-state/hooks';
import { useRouter } from 'next/router';
import React, { type FunctionComponent, useMemo } from 'react';
import {
  ADMIN_SETTINGS_PATH,
  CONNECTORS_PATH,
  ORGANIZATION_SOURCES_PATH
} from '../../../constants/general';
import {
  topNavIconButtonActiveSx,
  topNavIconButtonSx
} from '../../../constants/sx';
import { determineHref, openInNewTab } from '../../../helpers/link.helper';
import { useAuthorization } from '../../../hooks/use-authorization.hooks';
import { useCapabilities } from '../../../hooks/use-capabilities.hooks';
import {
  excludeActionFromInputMode,
  useCommandPalette
} from '../../../hooks/use-command-palette.hooks';
import { useManagedPopoverState } from '../../../hooks/use-managed-popover-state.hook';
import { useMenuTriggerTooltip } from '../../../hooks/use-menu-trigger-tooltip.hook';
import { AdminIcon } from '../../../icons/icons';
import {
  getLeftNavAdminMenuItems,
  PROFILING_ADMIN_ITEM_LABEL
} from '../left-nav-items/admin-navigation-items';
import { TopNavMenuPaper } from '../top-nav-menu-paper.styled';

export const AdminMenuPopover: FunctionComponent = () => {
  const router = useRouter();
  const adminPopupState = useManagedPopoverState({
    popupId: 'admin-menu-global'
  });

  const tooltipProps = useMenuTriggerTooltip(adminPopupState.isOpen);

  const handleClickAway = (_event: MouseEvent | TouchEvent) => {
    // Don't prevent default or stop propagation - let the click continue
    adminPopupState.close();
  };

  const { isAdministrator = false } = useAuthorization();
  const { connector, profiling } = useCapabilities();

  const adminMenuItems = useMemo(() => {
    return getLeftNavAdminMenuItems().filter((item) => {
      if (
        !connector &&
        (item.href === CONNECTORS_PATH ||
          item.href === ORGANIZATION_SOURCES_PATH)
      ) {
        return false;
      }
      if (!profiling.deployed && item.href === ADMIN_SETTINGS_PATH) {
        return false;
      }
      // The profiler (Pyroscope) only exists once profiling is deployed.
      if (!profiling.deployed && item.label === PROFILING_ADMIN_ITEM_LABEL) {
        return false;
      }
      return true;
    });
  }, [connector, profiling.deployed]);

  useCommandPalette(
    isAdministrator
      ? adminMenuItems.map((i) => ({
          label: `Go to ${i.label}`,
          description: i.description,
          adminOnly: true,
          action: (e) =>
            excludeActionFromInputMode(e, () => {
              if (i.external) {
                openInNewTab(determineHref(i.href));
              } else {
                router.push(determineHref(i.href));
              }
            }),
          keyboardShortcuts: i.shortcut
            ? { key: i.shortcut, modifier: { shift: true } }
            : undefined
        }))
      : []
  );

  if (!isAdministrator) {
    return null;
  }

  return (
    <React.Fragment>
      <ModTooltip title="Admin menu" {...tooltipProps}>
        <ModIconButton
          {...bindToggle(adminPopupState)}
          aria-label="Admin menu"
          variant="nav"
          active={adminPopupState.isOpen}
          sx={
            adminPopupState.isOpen
              ? topNavIconButtonActiveSx
              : topNavIconButtonSx
          }
        >
          <AdminIcon size={iconSize.xl} />
        </ModIconButton>
      </ModTooltip>
      <Popper
        {...bindPopper(adminPopupState)}
        placement="bottom-end"
        sx={{ zIndex: (theme) => theme.zIndex.snackbar }}
        disablePortal={false}
        modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
      >
        <ClickAwayListener
          onClickAway={handleClickAway}
          mouseEvent="onMouseDown"
        >
          <TopNavMenuPaper sx={{ pt: 1, pb: 2 }}>
            <MenuList sx={{ py: 0 }}>
              {adminMenuItems.map((item, index) =>
                item.external ? (
                  <ModMenuItem
                    key={`admin-menu-item-${index}`}
                    component="a"
                    href={determineHref(item.href)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => adminPopupState.close()}
                  >
                    {item.label}
                  </ModMenuItem>
                ) : (
                  <ModMenuItem
                    key={`admin-menu-item-${index}`}
                    onClick={() => {
                      adminPopupState.close();
                      router.push(determineHref(item.href));
                    }}
                  >
                    {item.label}
                  </ModMenuItem>
                )
              )}
            </MenuList>
          </TopNavMenuPaper>
        </ClickAwayListener>
      </Popper>
    </React.Fragment>
  );
};
